// Import react-circular-progressbar module and styles
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLeaderboardQuery } from "@/api/vercel";
import { format } from "date-fns";
import BottomNavbar from "@/components/bottom-navbar";
import CustomSwitch from "@/components/custom-switch";
import { Card } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { formatTimeOfDay } from "@/utils/time-format";

export default function HomePage() {
  const navigate = useNavigate();
  const [tracks, setTracks] = useState([]);
  const [races, setRaces] = useState([]);
  const [selectedTrack, setSelectedTrack] = useState("");
  const [selectedRace, setSelectedRace] = useState("");
  const [trackMap, setTrackMap] = useState(new Map());
  const [isLeaderboard, setIsLeaderboard] = useState(false);
  const [marketStartTime, setMarketStartTime] = useState(null);
  const [isTrackSelectOpen, setIsTrackSelectOpen] = useState(false);

  const {
    data: { data: leaderboardQueryData } = {},
    isError: isLeaderboardError,
    isLoading: isLeaderboardLoading,
  } = useLeaderboardQuery();

  const getGeneratedTime = () => {
    const now = new Date();
    const hours = now.getHours();

    if (hours < 9) {
      return "7:00AM";
    } else if (hours < 12) {
      return "9:00AM";
    } else if (hours < 15) {
      return "12:01PM";
    } else {
      return "3:00PM";
    }
  };

  const handleSwitchChange = (checked) => {
    setIsLeaderboard(checked);
    navigate(checked ? "/leaderboard" : "/home");
  };

  const handleTrackChange = (value) => {
    setSelectedTrack(value);
    if (trackMap.has(value)) {
      const trackRaces = Array.from(trackMap.get(value)).sort((a, b) => a - b);
      setRaces(trackRaces);
      setSelectedRace(trackRaces[0]?.toString() || "");
    }
  };

  const handleRaceChange = (value) => {
    setSelectedRace(value);
  };

  const findNextRace = () => {
    if (!leaderboardQueryData) return null;

    const now = new Date();
    let nextRace = null;
    let minTimeDiff = Infinity;

    // Iterate through all races to find the next one to jump
    Object.entries(leaderboardQueryData).forEach(([key, raceData]) => {
      if (!raceData.marketStartTime) return;

      const marketStartDate = new Date(raceData.marketStartTime);
      const timeDiff = marketStartDate.getTime() - now.getTime();

      // Only consider races that haven't started yet
      if (timeDiff > 0 && timeDiff < minTimeDiff) {
        minTimeDiff = timeDiff;
        // Split from the end to get date parts
        const parts = key.split("-");
        const year = parts.pop();
        const month = parts.pop();
        const day = parts.pop();
        const race = parts.pop();
        // Everything else is the track name
        const track = parts.join(" ");

        nextRace = {
          track,
          race,
          startTime: marketStartDate,
          date: `${day}-${month}-${year}`,
        };
      }
    });

    return nextRace;
  };

  const handleNextToJump = () => {
    const nextRace = findNextRace();
    if (nextRace) {
      setSelectedTrack(nextRace.track);
      setSelectedRace(nextRace.race);
      updateMarketStartTime(nextRace.track, nextRace.race);
    }
  };

  const updateMarketStartTime = (track, race) => {
    if (!leaderboardQueryData || !track || !race) {
      setMarketStartTime(null);
      return;
    }

    const today = format(new Date(), "dd-MM-yyyy");
    const raceKey = `${track.replace(/\s+/g, "-")}-${race}-${today}`;
    const raceData = leaderboardQueryData[raceKey];

    if (!raceData?.marketStartTime) {
      setMarketStartTime(null);
      return;
    }

    // Site-wide time format: "1:10 pm" in Sydney time
    setMarketStartTime(`Jumps @ ${formatTimeOfDay(raceData.marketStartTime)}`);
  };

  useEffect(() => {
    // Check if we came from the "Next to Jump" button
    const params = new URLSearchParams(window.location.search);
    if (params.get("nextToJump") === "true") {
      handleNextToJump();
      // Clear the query parameter
      navigate("/home", { replace: true });
    }
  }, [leaderboardQueryData]);

  useEffect(() => {
    // Process track and race data
    if (!leaderboardQueryData) return;

    const newTrackMap = new Map();
    Object.values(leaderboardQueryData).forEach((raceData) => {
      if (raceData.track) {
        if (!newTrackMap.has(raceData.track)) {
          newTrackMap.set(raceData.track, new Set());
        }
        newTrackMap.get(raceData.track).add(raceData.race);
      }
    });

    setTrackMap(newTrackMap);
    const tracksArray = Array.from(newTrackMap.keys()).sort();

    if (tracks.length === 0) {
      setTracks(tracksArray);
      if (tracksArray[0]) {
        const firstTrack = tracksArray[0];
        setSelectedTrack(firstTrack);
        const trackRaces = Array.from(newTrackMap.get(firstTrack)).sort(
          (a, b) => a - b,
        );
        setRaces(trackRaces);
        setSelectedRace(trackRaces[0]?.toString() || "");
      }
    }
  }, [leaderboardQueryData]);

  // Update market start time when track or race changes
  useEffect(() => {
    updateMarketStartTime(selectedTrack, selectedRace);
  }, [selectedTrack, selectedRace, leaderboardQueryData]);

  if (isLeaderboardLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spinner size="large" className="text-[#F30099]" />
      </div>
    );
  }

  if (isLeaderboardError) {
    return (
      <div className="flex flex-col h-screen items-center justify-center px-6 text-center">
        <svg
          className="w-12 h-12 text-red-500 mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          ></path>
        </svg>
        <h2 className="text-xl font-bold mb-2">Error loading race data</h2>
        <p className="text-gray-600 mb-6">
          We're having trouble connecting to our services. Please try again
          later.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-[#F30099] text-white rounded-md hover:bg-pink-700 transition duration-200"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="pb-[95px] max-w-md mx-auto">
      <div className="flex justify-between items-center mb-8 px-4 mt-6 align-middle">
        <div>
          <h1 className="text-xl font-extrabold">Lexi's Tips</h1>
          <p className="text-xs text-black mb-2">
            Generated {getGeneratedTime()} AEST
          </p>
        </div>
        <div className="flex items-center">
          <CustomSwitch checked={isLeaderboard} onChange={handleSwitchChange} />
        </div>
      </div>

      {/* Track and Race Selectors */}
      {marketStartTime && (
        <div className="col-span-2 mt-2">
          <p className="text-xs text-black text-right">{marketStartTime}</p>
        </div>
      )}

      {/* Leaderboard Section */}
      <div className="bottom-[80px] absolute z-20 bg-white rounded-[32px] p-4 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1),0_2px_4px_-1px_rgba(0,0,0,0.06)] w-full">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-bold">Leaderboard</h2>
        </div>
        <p className="text-xs text-black mb-4">
          Choose your race by rotate the dial.
        </p>

        {/* Circular Progress Component */}
        <div className="relative aspect-square">
          <div className="absolute inset-0 flex items-center justify-center"></div>
        </div>
        {/* Premium Plan Card */}
        <Card className="!bg-[#FCEDF7] p-4 flex justify-between items-center">
          <div>
            <div className="flex items-center gap-2">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="text-pink-500"
              >
                <path d="M12 2l1.5 4h4.5l-3.5 3 1.5 4-3.5-2-3.5 2 1.5-4-3.5-3h4.5z" />
              </svg>
              <h3 className="font-semibold text-md">Upgrade to Premium Plan</h3>
            </div>
            <p className="text-xs text-black">
              for higher accuracy, real-time update race condition, etc
            </p>
          </div>
          <button className="text-gray-600">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        </Card>
      </div>
      <BottomNavbar onNextToJump={handleNextToJump} />
    </div>
  );
}
